import React from 'react';
import useWebSocket from 'react-use-websocket';
import { z } from 'zod';

export interface OnDeployEvent {
    version: string;
    host: string;
}

export interface UseDeploymentShouldReloadProps {
    /** The current client version. */
    clientVersion: string;
    /** Function to fetch the current server version (called on mount). */
    getServerVersion: () => Promise<string | undefined>;
    /** The current host (e.g., archipro.co.nz). */
    host: string;
    /** The base WebSocket server URL. `/public` will be appended for deployment notifications. */
    webSocketUrl: string;
    /** Whether deployment reload detection is enabled. */
    enabled: boolean;
}

/** Zod schema for validating deployment messages from the WebSocket server. */
const DEPLOYMENT_MESSAGE_SCHEMA = z.object({
    type: z.literal('deployment/notify'),
    version: z.string(),
    host: z.string(),
});

/** Deployment message type inferred from the Zod schema. */
type DeploymentMessageType = z.infer<typeof DEPLOYMENT_MESSAGE_SCHEMA>;

/** Check if a message from the WebSocket is a heartbeat message. */
function isHeartbeatMessage(message: unknown): boolean {
    return !message || message === 'ping' || message === 'pong';
}

/**
 * Hook to detect when a new version has been deployed and determine if the
 * client should reload. Listens for deployment notifications via the public
 * WebSocket endpoint (no authentication required).
 *
 * @returns A callback that returns true if the client should reload.
 */
export const useDeploymentShouldReload = ({
    host,
    clientVersion,
    getServerVersion,
    webSocketUrl,
    enabled,
}: UseDeploymentShouldReloadProps) => {
    const serverVersion = React.useRef<string | undefined>();

    // Skip WebSocket in iframes
    const isIframe =
        typeof window !== 'undefined' && window.top !== window.self;
    const shouldConnect = enabled && !isIframe && !!webSocketUrl;

    // Use the public WebSocket endpoint for deployment notifications.
    // This doesn't require authentication and works for anonymous users.
    const publicWebSocketUrl = `${webSocketUrl}/public`;

    const { lastJsonMessage } = useWebSocket<DeploymentMessageType>(
        publicWebSocketUrl,
        {
            share: true,
            heartbeat: {
                timeout: 60_000,
                interval: 10_000,
            },
            filter: (message: MessageEvent) => {
                if (isHeartbeatMessage(message.data)) {
                    return false;
                }
                if (typeof message.data !== 'string') {
                    return false;
                }
                try {
                    DEPLOYMENT_MESSAGE_SCHEMA.parse(JSON.parse(message.data));
                    return true;
                } catch {
                    return false;
                }
            },
            shouldReconnect: () => shouldConnect,
            reconnectAttempts: 20,
            reconnectInterval: (lastAttemptNumber: number) => {
                if (lastAttemptNumber <= 2) {
                    return 2_000; // 2 seconds
                }
                // Use exponential backoff for reconnect attempts after the 3rd attempt.
                const baseDelay = 30_000; // 30 seconds
                const maxDelay = 3_600_000; // 1 hour
                const exponent = lastAttemptNumber - 3;
                return Math.min(baseDelay * Math.pow(2, exponent), maxDelay);
            },
        },
        shouldConnect,
    );

    // Fetch initial server version on mount
    React.useEffect(() => {
        if (enabled) {
            getServerVersion().then(
                (version) => (serverVersion.current = version),
            );
        }
    }, [enabled, getServerVersion]);

    // Handle deployment notifications
    React.useEffect(() => {
        if (!lastJsonMessage) {
            return;
        }

        if (lastJsonMessage.host !== host) {
            // This is a deployment event for a different host
            return;
        }

        console.log(
            `%c🎊 ${lastJsonMessage.version} has been released.`,
            'color: white; background: green; padding: 10px; border-radius: 5px;',
        );
        serverVersion.current = lastJsonMessage.version;
    }, [lastJsonMessage, host]);

    return React.useCallback(() => {
        if (
            enabled &&
            serverVersion.current !== undefined &&
            clientVersion !== serverVersion.current
        ) {
            console.log(
                `%cℹ️ New server version detected: "${serverVersion.current}". Reloading the app`,
                'color: white; background: green; padding: 10px; border-radius: 5px;',
            );
            return true;
        }
        return false;
    }, [clientVersion, enabled]);
};
